import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import './App.css';
import {
  createPracticeSession,
  createPracticeSessionFromQuestions,
  createPracticeSessionFromStandard,
  getCatalogStructure,
  gradeAnswers,
  login,
  signup,
  saveProgress,
  generateSimilarPractice,
} from './api';
import LoginModal from './components/LoginModal';
import NavBar from './components/NavBar';
import PracticeApp from './components/PracticeApp';
import HomePage from './pages/HomePage';
import StatesPage from './pages/StatesPage';
import GradeLevelsPage from './pages/GradeLevelsPage';
import GradeTopicsPage from './pages/GradeTopicsPage';
import MajorTopicPage from './pages/MajorTopicPage';
import TopicsPage from './pages/TopicsPage';
import PracticeTestPage from './pages/PracticeTestPage';

function App() {
  const navigate = useNavigate();

  // ── Auth state ──────────────────────────────────────────────────────────────
  const [authMode, setAuthMode] = useState('login');
  const [authForm, setAuthForm] = useState({ email: '', password: '' });
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    email: '',
    password: '',
  });

  // ── Practice session state ──────────────────────────────────────────────────
  const [sessionId, setSessionId] = useState(null);
  const [fileName, setFileName] = useState('Primary Mathematics');
  const [questions, setQuestions] = useState([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [practiceViewActive, setPracticeViewActive] = useState(false);
  const [practiceSessionId, setPracticeSessionId] = useState(null);
  const [practiceQuestions, setPracticeQuestions] = useState([]);
  const [practiceTopicName, setPracticeTopicName] = useState('');

  // ── UI state ────────────────────────────────────────────────────────────────
  const [status, setStatus] = useState({
    loadingSession: false,
    grading: false,
    savingProgress: false,
    generatingPractice: false,
    authLoading: false,
    error: '',
    info: '',
  });
  const [saveToast, setSaveToast] = useState('');
  const [pendingTopicQuestions, setPendingTopicQuestions] = useState(null);
  const [pendingTopicName, setPendingTopicName] = useState('');
  const [authModalOpen, setAuthModalOpen] = useState(false);

  useEffect(() => {
    if (!saveToast) return undefined;
    const timer = setTimeout(() => setSaveToast(''), 4000);
    return () => clearTimeout(timer);
  }, [saveToast]);

  // On initial load, never land on /practice — there's no in-memory session yet.
  // Send users to the states page so they pick a state, then a grade, then a topic.
  useEffect(() => {
    if (window.location.pathname.startsWith('/practice')) {
      navigate('/', { replace: true });
    }
    getCatalogStructure().catch(() => {});
    // Run only once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived values ──────────────────────────────────────────────────────────
  const displayQuestions = practiceViewActive ? practiceQuestions : questions;
  const displaySessionId = practiceViewActive ? practiceSessionId : sessionId;

  const selectedQuestion = useMemo(
    () => displayQuestions.find((q) => q.id === selectedQuestionId),
    [displayQuestions, selectedQuestionId]
  );
  const hasGradableAnswers = useMemo(
    () => displayQuestions.some((q) => shouldGradeQuestion(q)),
    [displayQuestions]
  );
  const selectedPracticeIndex = useMemo(
    () => displayQuestions.findIndex((q) => q.id === selectedQuestionId),
    [displayQuestions, selectedQuestionId]
  );
  const canGradeSelectedQuestion = useMemo(
    () => Boolean(displaySessionId && selectedQuestion && isAnswered(selectedQuestion)),
    [displaySessionId, selectedQuestion]
  );

  // ── Auth helpers ────────────────────────────────────────────────────────────
  const startPracticeSession = useCallback(async (email, password) => {
    setStatus((prev) => ({ ...prev, loadingSession: true, error: '' }));
    try {
      const data = await createPracticeSession(email, password);
      const loaded = (data.questions || []).map(normaliseQuestion);
      setSessionId(data.sessionId);
      setFileName(data.fileName || 'Primary Mathematics');
      setQuestions(loaded);
      setSelectedQuestionId(loaded[0]?.id || null);
      setStatus((prev) => ({ ...prev, loadingSession: false, error: '' }));
    } catch (err) {
      setStatus((prev) => ({
        ...prev,
        loadingSession: false,
        error: err.message || 'Failed to load practice questions',
      }));
    }
  }, []);

  const handleAuthSubmit = async () => {
    if (!authForm.email.trim() || !authForm.password.trim()) {
      setStatus((prev) => ({ ...prev, error: 'Username and password are required.' }));
      return;
    }
    setStatus((prev) => ({ ...prev, authLoading: true, error: '' }));
    try {
      const submitAuth = authMode === 'signup' ? signup : login;
      const data = await submitAuth(authForm.email.trim(), authForm.password);
      const nextAuthState = {
        isAuthenticated: true,
        email: data.email,
        password: authForm.password,
      };
      setAuthState(nextAuthState);
      setStatus((prev) => ({ ...prev, authLoading: false, error: '' }));
      setAuthModalOpen(false);
      if (pendingTopicQuestions) {
        await launchTopicSession(nextAuthState, pendingTopicQuestions, pendingTopicName);
        setPendingTopicQuestions(null);
        setPendingTopicName('');
      }
    } catch (err) {
      setStatus((prev) => ({
        ...prev,
        authLoading: false,
        error: err.message || 'Authentication failed',
      }));
    }
  };

  // ── Topic session launcher (called from browse pages) ───────────────────────
  // The selector names a standard (or sub-topic) and the server gathers the
  // questions, so the client never downloads the whole question bank.
  const launchTopicSession = useCallback(async (auth, selector, topicName) => {
    setStatus((prev) => ({ ...prev, loadingSession: true, error: '' }));
    try {
      const data = Array.isArray(selector)
        ? await createPracticeSessionFromQuestions(auth.email, auth.password, selector)
        : await createPracticeSessionFromStandard(auth.email, auth.password, selector);
      const loaded = (data.questions || []).map(normaliseQuestion);
      setPracticeSessionId(data.sessionId);
      setPracticeQuestions(loaded);
      setPracticeTopicName(topicName || '');
      setSelectedQuestionId(loaded[0]?.id || null);
      setPracticeViewActive(true);
      setStatus((prev) => ({ ...prev, loadingSession: false, error: '' }));
      navigate('/practice');
    } catch (err) {
      setStatus((prev) => ({
        ...prev,
        loadingSession: false,
        error: err.message || 'Failed to start practice session',
      }));
    }
  }, [navigate]);

  const handleTopicClick = useCallback((selector, topicName) => {
    if (!authState.isAuthenticated) {
      setPendingTopicQuestions(selector);
      setPendingTopicName(topicName || '');
      setAuthModalOpen(true);
      return;
    }
    launchTopicSession(authState, selector, topicName);
  }, [authState, launchTopicSession]);

  const handleAuthModalClose = useCallback(() => {
    setAuthModalOpen(false);
    setPendingTopicQuestions(null);
    setPendingTopicName('');
    setStatus((prev) => ({ ...prev, error: '' }));
  }, []);

  // ── Practice question field changes ────────────────────────────────────────
  const handleFieldChange = (field, value) => {
    const updater = (prev) =>
      prev.map((q) => {
        if (q.id !== selectedQuestionId) return q;
        const next = { ...q, [field]: value };
        if (field === 'studentAnswer') {
          const current = (value || '').trim();
          const lastGraded = (q.lastGradedAnswer || '').trim();
          next.answerChangedSinceLastGrade = current !== lastGraded;
        }
        return next;
      });
    if (practiceViewActive) {
      setPracticeQuestions(updater);
    } else {
      setQuestions(updater);
    }
  };

  const applyGradeResults = (results) => {
    if (!results?.length) return;
    const apply = (prev) => applyGradeResultsToQuestions(prev, results);
    if (practiceViewActive) {
      setPracticeQuestions(apply);
    } else {
      setQuestions(apply);
    }
  };

  const saveProgressSnapshot = async (
    questionsToSave,
    sessionIdToSave,
    successMessage = 'Progress saved.',
    { showToast = true } = {}
  ) => {
    if (!sessionIdToSave || !authState.email || !authState.password) return false;
    setStatus((prev) => ({ ...prev, savingProgress: true, error: '', info: '' }));
    try {
      const progressPayload = questionsToSave.map(toProgressPayload);
      const data = await saveProgress(
        authState.email,
        authState.password,
        sessionIdToSave,
        progressPayload
      );
      setStatus((prev) => ({ ...prev, savingProgress: false, error: '', info: '' }));
      if (showToast) {
        setSaveToast(data.message || successMessage);
      }
      return true;
    } catch (err) {
      setStatus((prev) => ({
        ...prev,
        savingProgress: false,
        error: err.message || 'Failed to save progress',
        info: '',
      }));
      return false;
    }
  };

  const handleGradeSelectedQuestion = async () => {
    const sid = displaySessionId;
    if (!sid || !selectedQuestionId || !selectedQuestion) return;
    const studentAnswer = (selectedQuestion.studentAnswer || '').trim();
    if (!studentAnswer) {
      setStatus((prev) => ({ ...prev, error: 'Please type an answer before grading this question.' }));
      return;
    }
    setStatus((prev) => ({ ...prev, grading: true, error: '' }));
    try {
      const data = await gradeAnswers(sid, [{ questionId: selectedQuestionId, studentAnswer }]);
      const results = data.results || [];
      const updatedQuestions = applyGradeResultsToQuestions(displayQuestions, results);
      applyGradeResults(results);
      const progressSaved = await saveProgressSnapshot(
        updatedQuestions,
        sid,
        'Answer and progress saved.'
      );
      setStatus((prev) => ({
        ...prev,
        grading: false,
        error: progressSaved ? '' : prev.error,
      }));
    } catch (err) {
      setStatus((prev) => ({ ...prev, grading: false, error: err.message || 'Grading failed' }));
      return;
    }
  };

  const handleGrade = async () => {
    const sid = displaySessionId;
    if (!sid) return;
    const answersToGrade = displayQuestions
      .filter(shouldGradeQuestion)
      .map((q) => ({ questionId: q.id, studentAnswer: (q.studentAnswer || '').trim() }));
    if (!answersToGrade.length) {
      setStatus((prev) => ({
        ...prev,
        error: 'No answers need grading right now. Update an answer or retry incorrect ones.',
      }));
      return;
    }
    setStatus((prev) => ({ ...prev, grading: true, error: '' }));
    try {
      const data = await gradeAnswers(sid, answersToGrade);
      const results = data.results || [];
      const updatedQuestions = applyGradeResultsToQuestions(displayQuestions, results);
      applyGradeResults(results);
      const progressSaved = await saveProgressSnapshot(
        updatedQuestions,
        sid,
        'Answers and progress saved.'
      );
      setStatus((prev) => ({
        ...prev,
        grading: false,
        error: progressSaved ? '' : prev.error,
      }));
    } catch (err) {
      setStatus((prev) => ({ ...prev, grading: false, error: err.message || 'Grading failed' }));
      return;
    }
  };

  const handleGeneratePractice = async () => {
    if (!sessionId || !selectedQuestionId || !authState.email || !authState.password) return;
    setStatus((prev) => ({ ...prev, generatingPractice: true, error: '' }));
    try {
      const { questions: generated } = await generateSimilarPractice(
        authState.email,
        authState.password,
        sessionId,
        selectedQuestionId
      );
      if (!generated?.length) {
        setStatus((prev) => ({ ...prev, generatingPractice: false, error: 'No practice questions were generated.' }));
        return;
      }
      const data = await createPracticeSessionFromQuestions(
        authState.email,
        authState.password,
        generated
      );
      const withMeta = (data.questions || []).map(normaliseQuestion);
      setPracticeSessionId(data.sessionId);
      setPracticeQuestions(withMeta);
      setSelectedQuestionId(withMeta[0]?.id || null);
      setPracticeViewActive(true);
      setStatus((prev) => ({ ...prev, generatingPractice: false, error: '' }));
      navigate('/practice');
    } catch (err) {
      setStatus((prev) => ({
        ...prev,
        generatingPractice: false,
        error: err.message || 'Failed to generate practice problems',
      }));
    }
  };

  const handleNextQuestion = async () => {
    const idx = displayQuestions.findIndex((q) => q.id === selectedQuestionId);
    if (idx < 0 || idx >= displayQuestions.length - 1) return;
    if (displaySessionId && authState.email && authState.password) {
      const saved = await saveProgressSnapshot(
        displayQuestions,
        displaySessionId,
        '',
        { showToast: false }
      );
      if (!saved) return;
    }
    setSelectedQuestionId(displayQuestions[idx + 1].id);
  };

  // ── Modals ──────────────────────────────────────────────────────────────────
  // Visitors land on the home page first; auth is only requested once they
  // actually start practising (or click login themselves).
  const showAuthModal = !authState.isAuthenticated && authModalOpen;

  return (
    <div className="app-shell">
      <NavBar
        authEmail={authState.email}
        onLoginClick={() => setAuthModalOpen(true)}
      />

      {status.error && <div className="error global-error">{status.error}</div>}

      <Routes>
        <Route
          path="/"
          element={
            <HomePage
              isAuthenticated={authState.isAuthenticated}
              onRequestLogin={() => setAuthModalOpen(true)}
            />
          }
        />
        <Route
          path="/states"
          element={<StatesPage />}
        />
        <Route
          path="/state/:stateName"
          element={<GradeLevelsPage />}
        />
        <Route
          path="/state/:stateName/grade/:gradeName"
          element={
            <GradeTopicsPage
              authEmail={authState.email}
              authPassword={authState.password}
            />
          }
        />
        <Route
          path="/state/:stateName/grade/:gradeName/practice-test"
          element={<PracticeTestPage />}
        />
        <Route
          path="/state/:stateName/grade/:gradeName/topic/:majorTopicName"
          element={
            <MajorTopicPage
              onStartSkill={handleTopicClick}
              authEmail={authState.email}
              authPassword={authState.password}
            />
          }
        />
        <Route
          path="/topics"
          element={<TopicsPage onTopicClick={handleTopicClick} />}
        />
        <Route
          path="/practice"
          element={
            <PracticeApp
              sessionId={sessionId}
              fileName={fileName}
              practiceTopicName={practiceTopicName}
              questions={questions}
              selectedQuestionId={selectedQuestionId}
              onSelectQuestion={setSelectedQuestionId}
              selectedQuestion={selectedQuestion}
              onFieldChange={handleFieldChange}
              onGradeCurrent={handleGradeSelectedQuestion}
              canGradeCurrent={canGradeSelectedQuestion}
              onGrade={handleGrade}
              onGeneratePractice={handleGeneratePractice}
              onNextQuestion={handleNextQuestion}
              practiceViewActive={practiceViewActive}
              practiceSessionId={practiceSessionId}
              practiceQuestions={practiceQuestions}
              status={status}
              hasGradableAnswers={hasGradableAnswers}
              selectedPracticeIndex={selectedPracticeIndex}
              authEmail={authState.email}
              authPassword={authState.password}
            />
          }
        />
      </Routes>

      <LoginModal
        showAuthModal={showAuthModal}
        authMode={authMode}
        setAuthMode={setAuthMode}
        authForm={authForm}
        setAuthForm={setAuthForm}
        status={status}
        onAuthSubmit={handleAuthSubmit}
        onClose={handleAuthModalClose}
      />

      {(status.loadingSession || status.generatingPractice) && (
        <div className="session-loading">
          <div className="card">
            {status.generatingPractice
              ? 'Generating practice problems...'
              : 'Loading questions...'}
          </div>
        </div>
      )}
      {saveToast && <div className="toast-message">{saveToast}</div>}
    </div>
  );
}

function normaliseQuestion(q) {
  return {
    ...q,
    studentAnswer: q.studentAnswer || '',
    hasBeenGraded: Boolean(q.hasBeenGraded),
    lastGradedAnswer: q.lastGradedAnswer || '',
    answerChangedSinceLastGrade: Boolean(q.answerChangedSinceLastGrade),
  };
}

function applyGradeResultsToQuestions(questions, results) {
  if (!results?.length) return questions;
  const resultsByQuestionId = new Map(
    results.map((result) => [result.questionId, result])
  );
  return questions.map((question) => {
    const result = resultsByQuestionId.get(question.id);
    if (!result) return question;
    return {
      ...question,
      grading: result,
      gradingAnimationVersion: (question.gradingAnimationVersion || 0) + 1,
      hasBeenGraded: true,
      lastGradedAnswer: (question.studentAnswer || '').trim(),
      answerChangedSinceLastGrade: false,
    };
  });
}

function toProgressPayload(question) {
  return {
    questionId: question.id,
    dbIndex: question.dbIndex,
    dbSource: question.dbSource,
    studentAnswer: question.studentAnswer || '',
    hasBeenGraded: Boolean(question.hasBeenGraded),
    lastGradedAnswer: question.lastGradedAnswer || '',
    answerChangedSinceLastGrade: Boolean(question.answerChangedSinceLastGrade),
    grading: question.grading || null,
  };
}

function isAnswered(question) {
  return Boolean((question.studentAnswer || '').trim());
}

function shouldGradeQuestion(question) {
  if (!isAnswered(question)) return false;
  if (!question.hasBeenGraded) return Boolean(question.answerChangedSinceLastGrade);
  if (question.grading && question.grading.correct === false) return true;
  return Boolean(question.answerChangedSinceLastGrade);
}

export default App;
